import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { FilterMatchMode, FilterMetadata, FilterService, MenuItem, MessageService, SelectItem, TreeNode } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabel } from 'primeng/floatlabel';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { SplitButtonModule } from 'primeng/splitbutton';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';

import { firstValueFrom, forkJoin } from 'rxjs';
import { SETTINGS, Settings } from '../../../../app.settings';
import { PrintLabelComponent, ProcessingComponent } from '../../../../components';

import {
  CatalogAdminService,
  IAppUser,
  ILocation,
  InsectaryDashboardService,
  IOrderItemInstanceUpdateShippingRequest,
  ISearchFilterValue,
  ISearchRequest,
  ISearchRequestFilter,
  ISearchResult,
  ISearchSort,
  OrderItemInstanceService,
  OrderItemInstanceStatus,
  OrderService,
  UnitOfMeasureAdminService
} from '../../../../services/insecta';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { dateToIsoString, getItemDescriptionColumn, isNotEmpty, isStringEmpty, isStringEqual, toShortDate } from '../../../../common/functions';
import { successMsg, warningMsg } from '../../../../common/messages';
import { aggregateValidation, VALIDATION_PREFIX } from '../../../../common/validation';
import { AppInitService, IInsectaPermissions } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { ModsService } from '../../../../services/insecta/mods.service';

pdfMake.vfs = pdfFonts.vfs;
pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf'
  }
};

@Component({
  selector: 'app-insectary-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    FileUploadModule,
    FloatLabel,
    FloatLabelModule,
    FormsModule,
    ImageModule,
    InputTextModule,
    InputNumberModule,
    TableModule,
    RouterModule,
    ProcessingComponent,
    PaginatorModule,
    DialogModule,
    Breadcrumb,
    PrintLabelComponent,
    SplitButtonModule,
    TooltipModule,
    TreeSelectModule
  ],
  templateUrl: './substrate-prep.component.html'
})
export class SubstratePrepComponent implements OnInit {
  @Input() appUser!: IAppUser;
  @ViewChild('dt') dt!: Table;

  userSettingsJson: { [key: string]: any } = {};
  route: any;
  permissions!: IInsectaPermissions;

  showBatchRecipeModel: boolean = false;
  showSetupDateModel: boolean = false;
  showExportModel: boolean = false;
  showPrintModal: boolean = false;
  deliverShipItems: MenuItem[] = [];
  deliveryDlgVars: any = {
    deliveryLocation: undefined,
    isVisible: false,
    locations: []
  };
  isDeliverDialogValid: boolean = false;
  shipDlgVars: any = {
    shippingInfo: <IOrderItemInstanceUpdateShippingRequest>{},
    isVisible: false
  };
  isShipDialogValid: boolean = false;

  setUpDate: Date | null = null;
  selectedOrderItemInstances: any[] = [];
  selectedOrderItemInstanceIds: any[] = [];

  cols: any[] = [];
  activeColumnFilters: Set<string> = new Set<string>();
  filtersActive: boolean = false;

  customMatchModeOptions: SelectItem[] = [
    { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
    { label: 'Contains', value: FilterMatchMode.CONTAINS },
    { label: 'Not Contains', value: FilterMatchMode.NOT_CONTAINS },
    { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
    { label: 'Equals', value: FilterMatchMode.EQUALS },
    { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Has Value', value: 'isNotEmpty' }
  ];
  dateMatchModeOptions: SelectItem[] = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Date Is Not', value: FilterMatchMode.DATE_IS_NOT },
  { label: 'Date Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'Date After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

numericMatchModeOptions: SelectItem[] = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Less Than or Equal', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Greater Than or Equal', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
  { label: 'Is Empty', value: 'isEmpty' },
  { label: 'Has Value', value: 'isNotEmpty' }
];

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Dashboard:SubstratePrep`,
    breadcrumbItems: <MenuItem[]>[]
  };

  columns: string[] = [
    'InstanceSequence',
    'DeliveryDate',
    'RequesterName',
    'Note',
    'SubstrateName',
    'SubstrateQuantity',
    'ContainerName',
    'StandardFillAmount',
    'SubstrateAmount',
    'SubstrateSetupDate',
    'DeliveryLocation',
    'InsectQuantity',
    'InfestationRate',
    'VendorDelay',
    'DelayNote'
  ];

  selectAllToggle: boolean = false;
  orderItemInstances: any[] = [];

  skip: number = 0;
  take: number = 10;
  totalCount: number = 0;

  insectaryDashboardService: InsectaryDashboardService = inject(InsectaryDashboardService);
  searchRequest: ISearchRequest = {
    columns: [],
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };

  itemDescName: string = '';
  siteName: string = '';
  amountOfOverage!: number;
  instancesWithNullSubstrateAmount: any[] = [];

  isModsRecipeEnabled: boolean = false;
  isRecipesLookupForbidden: boolean = false;

  constructor(
    @Inject(SETTINGS) private settings: Settings,
    private msgService: MessageService,
    private orderItemInstanceService: OrderItemInstanceService,
    private appInit: AppInitService,
    private appInsights: AppInsightsService,
    private uomApiService: UnitOfMeasureAdminService,
    private modsApiService: ModsService,
    private catalogService: CatalogAdminService,
    private orderService: OrderService,
    private filterService: FilterService
  ) {
    this.pageVars.pageId = `${settings.environment}:Dashboard:InsectaryDashboard`;
    appInsights.trackPageView(this.pageVars.pageId, `/dashboard/SubstratePrep`);
  }

  ngOnInit(): void {
    this.getRecipesLookup();
    this.pageVars.isReady = false;
    this.appUser = this.appInit.appUser;
    this.permissions = this.appInit.permissions;
    this.filterService.register('isEmpty', (value: any): boolean => {
      return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    });
    this.filterService.register('isNotEmpty', (value: any): boolean => {
      return value !== undefined && value !== null && (typeof value === 'string' && value.trim() !== '');
    });

    if (this.appUser) {
      if (!isStringEmpty(this.appUser.userSettingsJson) || this.appUser.userSettingsJson === '{}') {
        this.userSettingsJson = JSON.parse(this.appUser.userSettingsJson);
      } else {
        this.userSettingsJson = {};
      }

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
      this.siteName = this.userSettingsJson['Default Site'] || '';
    }

    if (this.permissions.canViewInsectaryDashboard && this.permissions.canViewRequestorDashboard) {
      this.pageVars.breadcrumbItems = [{ label: 'Insectary Dashboard', routerLink: '/insectarydashboard' }, { label: 'Substrate Prep' }];
    } else {
      this.pageVars.breadcrumbItems = [{ label: 'Dashboard', routerLink: '/insectarydashboard' }, { label: 'Substrate Prep' }];
    }

    this.initializeColumns();

    this.deliverShipItems = [
      { label: 'Add Delivery Location', icon: 'pi pi-map-marker', command: () => this.deliveryDlgShow() },
      { label: 'Add Shipping Info', icon: 'pi pi-truck', command: () => this.shipDlgShow() },
      { label: 'Mark as Delivered', icon: 'pi pi-check', command: () => this.markAsDelivered() }
    ];
  }

  initializeColumns() {
    this.cols = [
      { field: 'InstanceSequence', header: 'Order Item Instance ID', type: 'text', template: 'link' },
      { field: 'DeliveryDate', header: 'Delivery Date', type: 'date' },
      { field: 'RequesterName', header: 'Requester', type: 'text' },
      { field: 'Note', header: 'Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'SubstrateName', header: 'Substrate Name', type: 'text', template: 'variant', style: { 'min-width': '250px' } },
      { field: 'SubstrateQuantity', header: 'Quantity', type: 'numeric' },
      { field: 'ContainerName', header: 'Container', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } },
      { field: 'StandardFillAmount', header: 'Standard Fill', type: 'boolean', template: 'boolean' },
      { field: 'SubstrateAmount', header: 'Fill Amount', type: 'text' },
      { field: 'SubstrateSetupDate', header: 'Set-Up Date', type: 'date' },
      { field: 'DeliveryLocation', header: 'Delivery Location (Requested)', type: 'text' },
      { field: 'InsectQuantity', header: 'Insect Quantity', type: 'numeric' },
      { field: 'InfestationRate', header: 'Infestation Rate', type: 'numeric' },
      { field: 'VendorDelay', header: 'Vendor Delay', type: 'boolean', template: 'boolean' },
      { field: 'DelayNote', header: 'Delay Note', type: 'text', template: 'tooltip', style: { 'min-width': '250px' } }
    ];

    this.syncColumnState();
  }

  private syncColumnState() {
    const stateKey = 'substrate-prep-table';
    try {
      const storedState = localStorage.getItem(stateKey);
      if (!storedState) return;

      const state = JSON.parse(storedState);
      const columnOrder = state?.columnOrder as string[] | undefined;

      if (columnOrder && columnOrder.length !== this.cols.length) {
        delete state.columnOrder;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage column order', e);
    }
  }

  getColumnHeaderStyle(columnName: string): any {
    return this.activeColumnFilters.has(columnName) ? { 'background-color': '#e0f7fa', 'border-bottom': '2px solid #006064' } : {};
  }

  handleTableEvents(event: TableLazyLoadEvent) {
    this.pageVars.isReady = false;
    this.activeColumnFilters.clear();

    this.searchRequest.filters = [];
    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'SiteName',
      logic: 'and',
      values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
    });

    this.columns.forEach((columnName) => {
      const columnFilters = event.filters![columnName] as FilterMetadata[];
      if (columnFilters && columnFilters.length > 0) {
        const tableFilter = <ISearchRequestFilter>{
          fieldname: columnName,
          logic: columnFilters[0].operator,
          values: []
        };
        columnFilters.forEach((filterValue) => {
          if (filterValue.matchMode === 'isEmpty' || filterValue.matchMode === 'isNotEmpty') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode.toUpperCase(),
              value: ''
            });
          } else if (filterValue.value !== null && filterValue.value !== undefined && filterValue.value !== '') {
            tableFilter.values.push(<ISearchFilterValue>{
              operator: filterValue.matchMode || `contains`,
              value: filterValue.matchMode?.startsWith(`date`) ? toShortDate(filterValue.value) : `${filterValue.value}`
            });
          }
        });

        if (tableFilter.values.length > 0) {
          this.searchRequest.filters.push(tableFilter);
          this.activeColumnFilters.add(columnName);
        }
      }
    });

    this.searchRequest.sorts = [];
    if (isNotEmpty(event.sortField)) {
      this.searchRequest.sorts.push(<ISearchSort>{ name: event.sortField, direction: event.sortOrder === 1 ? 'ASC' : 'DESC' });
    } else {
      this.searchRequest.sorts.push(<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' });
    }

    this.searchRequest.skip = event.first;
    this.searchRequest.take = event.rows ?? 10;
    this.filtersActive = this.activeColumnFilters.size > 0 || isNotEmpty(event.sortField);
    this.executeSearch();
  }

  clearFilters(table: Table) {
    table.sortField = null;
    table.sortOrder = 1;
    table.clear();
    if (table.filters) {
    Object.keys(table.filters).forEach((key) => {
      const col = this.cols?.find((c: any) => c.field === key);
      const filters = table.filters[key] as any[];
      if (Array.isArray(filters)) {
        filters.forEach((f) => {
          if (f.matchMode === 'isEmpty' || f.matchMode === 'isNotEmpty') {
          if (col?.type === 'date') {
            f.matchMode = 'dateIs';
          } else if (col?.type === 'numeric') {
            f.matchMode = 'equals';
          } else {
            f.matchMode = 'contains';
          }
            f.value = null;
          }
        });
      }
    });
  }
    this.activeColumnFilters.clear();
    this.filtersActive = false;

    try {
      const stateKey = 'substrate-prep-table';
      const storedState = localStorage.getItem(stateKey);
      if (storedState) {
        const state = JSON.parse(storedState);
        delete state.sortField;
        delete state.sortOrder;
        delete state.filters;
        localStorage.setItem(stateKey, JSON.stringify(state));
      }
    } catch (e) {
      console.error('Error updating local storage in clearFilters', e);
    }

    setTimeout(() => {
      this.searchRequest.filters = [];
      this.searchRequest.filters.push(<ISearchRequestFilter>{
        fieldname: 'SiteName',
        logic: 'and',
        values: [<ISearchFilterValue>{ operator: 'equals', value: this.siteName }]
      });
      this.searchRequest.sorts = [{ name: 'DeliveryDate', direction: 'ASC' }];
      this.searchRequest.skip = 0;
      this.searchRequest.take = this.take;
      this.executeSearch();
    }, 100);
  }

  resetPage(table: Table) {
    table.clear();
    table.clearState();
    window.location.reload();
  }

  selectRowClick() {
    this.selectAllToggle = this.selectedOrderItemInstances.length == this.totalCount;
  }

  toggleSelectAll(checkAll: boolean) {
    if (!checkAll) {
      this.selectedOrderItemInstances = [];
      this.selectAllToggle = false;
      return;
    }
    const recipeId = new Set(this.selectedOrderItemInstances.map((instance: any) => instance.RecipeJson.recipeId));
    if (recipeId.size > 1) {
      this.msgService.add({
        severity: 'warn',
        summary: 'Recipe mismatch',
        detail: 'Instances must have same recipe to Get Batch Recipe.'
      });
      return;
    }

    this.pageVars.isReady = false;

    const selectAllSearch = this.searchRequest;
    selectAllSearch.columns = ['InstanceId'];
    selectAllSearch.skip = 0;
    selectAllSearch.take = this.totalCount;

    firstValueFrom(this.insectaryDashboardService.getSubstratePrepTable(selectAllSearch))
      .then((response: ISearchResult) => {
        const results = JSON.parse(response?.results ?? `[]`);
        results.map((instance: any) => {
          instance.RecipeJson = JSON.parse(instance.RecipeJson || '{}');
        });
        this.selectedOrderItemInstances = results;
        this.selectAllToggle = true;
      })
      .catch((err) => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'SelectAll' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  executeSearch() {
    const request = this.searchRequest;
    request.columns = [...this.columns, this.itemDescName];

    firstValueFrom(this.insectaryDashboardService.getSubstratePrepTable(request))
      .then(async (response) => {
        this.totalCount = response?.totalCount || 0;
        this.orderItemInstances = JSON.parse(response?.results ?? `[]`);
        this.orderItemInstances.map((instance) => {
          instance.RecipeJson = JSON.parse(instance.RecipeJson || '{}');
        });
        this.selectedOrderItemInstanceIds = [];
        this.selectedOrderItemInstances = [];
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'ExecuteSearch' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  addSetupDate() {
    this.showSetupDateModel = true;
  }

  onSaveSetupDate() {
    if (this.selectedOrderItemInstances.length > 0) {
      this.pageVars.isReady = false;

      const selectedIds = new Set(this.selectedOrderItemInstances.map((x: any) => x.InstanceId));
      const selectedInstances = this.orderItemInstances.filter((instance: any) => selectedIds.has(instance.InstanceId));
      const instanceArgs = selectedInstances.map(
        (selectedInstance) =>
          <any>{
            id: selectedInstance.InstanceId,
            orderId: selectedInstance.OrderId,
            orderItemId: selectedInstance.ItemId,
            substrateSetupDate: this.setUpDate ? dateToIsoString(this.setUpDate) : null,
            insectSetupDate: null,
            itemType: selectedInstance.ItemType
          }
      );
      const requests = instanceArgs.map((args) =>
        this.orderItemInstanceService.addSetupDateOrderItemInstance(args.orderId, args.orderItemId, args.id, args)
      );

      firstValueFrom(forkJoin(requests))
        .then(() => {
          successMsg(this.msgService, 'Set up date added successfully');
          this.executeSearch();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'AddSetupDate' });
        })
        .finally(() => {
          this.pageVars.isReady = true;
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected');
    }

    this.showSetupDateModel = false;
  }

  onCancelSetupDate() {
    this.showSetupDateModel = false;
  }

  deliveryDlgShow() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected.');
      return;
    }
    this.pageVars.isReady = false;

    firstValueFrom(this.orderService.getLocationsForHierarchy())
      .then((response) => {
        const siteLocations = response.find((x) => isStringEqual(x.name, this.siteName))?.children ?? [];
        this.deliveryDlgVars.deliveryLocation = undefined;
        this.deliveryDlgVars.locations = siteLocations.map((x) => this.buildNode(x));
        this.deliveryDlgVars.isVisible = true;
        this.computeDeliverDialogValidation(false);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'LoadLocations' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  deliveryDlgSave() {
    if (!this.computeDeliverDialogValidation(true)) {
      return;
    }
    this.pageVars.isReady = false;

    const selectedIds = this.selectedOrderItemInstances.map((x) =>
      typeof x === 'string' ? x : x.InstanceId
    );

    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceDeliveryLocation(selectedIds, this.deliveryDlgVars.deliveryLocation.data.id))
      .then(() => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        this.deliveryDlgVars.isVisible = false;
        successMsg(this.msgService, 'Delivery location updated successfully');
        this.executeSearch();
        this.computeDeliverDialogValidation(false);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'SaveDeliveryLocation' });
        this.pageVars.isReady = true;
      });
  }

  deliveryDlgCancel() {
    this.deliveryDlgVars.isVisible = false;
  }

  computeDeliverDialogValidation(showToast: boolean) {
    this.isDeliverDialogValid = aggregateValidation(
      this.msgService,
      [{ valid: isNotEmpty(this.deliveryDlgVars.deliveryLocation), message: 'A delivery location must be selected.' }],
      { prefix: VALIDATION_PREFIX, silent: !showToast }
    );
    return this.isDeliverDialogValid;
  }

  buildNode(location: ILocation): TreeNode {
    location.locationTypeId = location.locationTypeId.toUpperCase();
    location.containmentTypeId = location.containmentTypeId?.toUpperCase();
    if (isNotEmpty(location.address)) {
      location.address!.privateUserId = location.address!.privateUserId?.toUpperCase();
    }
    return <TreeNode>{
      key: location.id,
      label: location.name,
      data: location,
      children: location.children.map((x) => this.buildNode(x))
    };
  }

  shipDlgShow() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected.');
      return;
    }

    this.shipDlgVars.shippingInfo = <IOrderItemInstanceUpdateShippingRequest>{
      instances: this.selectedOrderItemInstances,
      shippingVendor: null,
      shippingTrackingNumber: null,
      shippingDate: null
    };

    this.shipDlgVars.isVisible = true;
  }

  shipDlgSave() {
    if (!this.computeShipDialogValidation(true)) {
      return;
    }
    this.pageVars.isReady = false;

    const selectedIds = this.selectedOrderItemInstances.map((x) =>
      typeof x === 'string' ? x : x.InstanceId
    );

    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceShipping({
      ...this.shipDlgVars.shippingInfo,
      instances: selectedIds
    }))
      .then(() => {
        this.selectedOrderItemInstances = [];
        this.selectAllToggle = false;
        this.shipDlgVars.shippingInfo = <IOrderItemInstanceUpdateShippingRequest>{};
        this.shipDlgVars.isVisible = false;
        successMsg(this.msgService, 'Shipping information updated successfully');
        this.executeSearch();
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'SaveShipping' });
        this.pageVars.isReady = true;
      });
  }

  shipDlgCancel() {
    this.shipDlgVars.isVisible = false;
    this.shipDlgVars.shippingInfo = <IOrderItemInstanceUpdateShippingRequest>{};
  }

  computeShipDialogValidation(showToast: boolean) {
    const info: IOrderItemInstanceUpdateShippingRequest = this.shipDlgVars.shippingInfo || <IOrderItemInstanceUpdateShippingRequest>{};
    this.isShipDialogValid = aggregateValidation(
      this.msgService,
      [
        { valid: !!info.shippingVendor, message: 'Shipping vendor is required.' },
        { valid: !!info.shippingTrackingNumber, message: 'Tracking number is required.' },
        { valid: !!info.shippingDate, message: 'Shipping date is required.' }
      ],
      { prefix: VALIDATION_PREFIX, silent: !showToast }
    );
    return this.isShipDialogValid;
  }

  onShippingVendorChange() {
    this.computeShipDialogValidation(false);
  }

  onShippingTrackingChange() {
    this.computeShipDialogValidation(false);
  }

  onShippingDateChange() {
    this.computeShipDialogValidation(false);
  }

  markAsDelivered() {
    if (this.selectedOrderItemInstances.length === 0) {
      warningMsg(this.msgService, 'No order item instance selected.');
      return;
    }
    this.pageVars.isReady = false;

    const selectedIds = this.selectedOrderItemInstances.map((x) =>
      typeof x === 'string' ? x : x.InstanceId
    );

    firstValueFrom(this.orderItemInstanceService.updateOrderItemInstanceWorkflowStatus(selectedIds, OrderItemInstanceStatus.Delivered))
      .then(() => {
        this.selectedOrderItemInstances = [];
        successMsg(this.msgService, 'Instances marked as delivered successfully');
        this.executeSearch();
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'MarkAsDelivered' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  getBatchRecipe() {
    const isHavingRecipe = this.selectedOrderItemInstances.some((x) => {
      try {
        if (x.RecipJson === null) return true;
        if (typeof x.RecipeJson === 'object' && Object.keys(x.RecipeJson).length === 0) return true;
        if (typeof x.RecipeJson === 'string' && x.RecipeJson.trim() === '{}') return true;
      } catch {
        return true;
      }
      return false;
    });
    if (isHavingRecipe) {
      warningMsg(this.msgService, 'The system is unable to get ingredients because the Substrate does not have an associated Recipe defined');
      return;
    }
    const recipeId = new Set(this.selectedOrderItemInstances.map((instance: any) => instance.RecipeJson.recipeId));
    if (recipeId.size > 1) {
      this.msgService.add({
        severity: 'warn',
        summary: 'Recipe mismatch',
        detail: 'Instances must have same recipe to Get Batch Recipe.'
      });
      return;
    }
    if (recipeId === undefined || recipeId === null || recipeId.size === 0) {
      warningMsg(this.msgService, 'Operation not allowed without Recipe.');
      return;
    }
    if (recipeId.size > 1) {
      warningMsg(this.msgService, 'Instances must have same recipe to Get Batch Recipe.');
      return;
    }
    this.instancesWithNullSubstrateAmount = this.selectedOrderItemInstances.filter(
      (instance: any) => instance.SubstrateAmount === null || instance.SubstrateAmount === 0
    );
    this.showBatchRecipeModel = true;
  }

  async exportThisList() {
    this.pageVars.isReady = false;
    try {
      const exportRequest = this.searchRequest;
      exportRequest.columns = [...this.columns.filter((x) => !x.startsWith('ItemDescription')), this.itemDescName];
      exportRequest.skip = 0;
      exportRequest.sorts = [];
      exportRequest.take = this.totalCount;
      await this.insectaryDashboardService.getSubstratePrepExport(exportRequest);
    } catch (err) {
      notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'ExportInstances' });
    } finally {
      this.pageVars.isReady = true;
    }
  }

  onCancelExport() {
    this.showExportModel = false;
  }

  printLabels() {
    if (this.selectedOrderItemInstances.length > 0) {
      this.pageVars.isReady = false;

      firstValueFrom(this.catalogService.getBartenderConfig())
        .then((config) => {
          const hasServerUrl = config.serverUrl && config.serverUrl.trim() !== '';
          if (!hasServerUrl) {
            warningMsg(this.msgService, 'Printer setup has not been configured. Please contact your administrator.');
            return;
          }
          this.selectedOrderItemInstanceIds = this.selectedOrderItemInstances
            .map((x) => (typeof x === 'string' ? x : x.InstanceId))
            .filter((id) => id != null && id !== '' && id !== '00000000-0000-0000-0000-000000000000');

          if (this.selectedOrderItemInstanceIds.length === 0) {
            warningMsg(this.msgService, 'Selected items do not have valid instance IDs.');
            return;
          }

          afterRender(() => {
            this.showPrintModal = true;
          });
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'PrintLabelsConfig' });
        })
        .finally(() => {
          this.pageVars.isReady = true;
        });
    } else {
      warningMsg(this.msgService, 'No order item instance selected.');
    }
  }

  afterPrintLabels() {
    this.pageVars.isReady = true;
  }

  onCancelBatchRecipe() {
    this.showBatchRecipeModel = false;
    this.instancesWithNullSubstrateAmount = this.instancesWithNullSubstrateAmount.map((instance: any) => (instance.SubstrateAmount = null));
    this.amountOfOverage = 0;
  }

  onCalculateBatchRecipe() {
    if (this.amountOfOverage == null || this.amountOfOverage < 0 || isNaN(this.amountOfOverage) || this.amountOfOverage === 0) {
      warningMsg(this.msgService, 'Please enter a valid overage amount.');
      return;
    }
    if (
      this.instancesWithNullSubstrateAmount.length > 0 &&
      this.instancesWithNullSubstrateAmount.some((instance: any) => instance.SubstrateAmount === null || instance.SubstrateAmount === 0)
    ) {
      warningMsg(this.msgService, 'Please enter valid substrate amount for all instances.');
      return;
    }
    const substrateAmounts: string[] = [];
    this.instancesWithNullSubstrateAmount.map((instance: any) => {
      const totalQuatity = instance.SubstrateAmount * instance.SubstrateQuantity;
      substrateAmounts.push(totalQuatity + ' ml');
    });

    this.selectedOrderItemInstances.map((instance: any) => {
      if (!this.instancesWithNullSubstrateAmount.some((x) => x.InstanceId === instance.InstanceId)) {
        const totalQuatity = Number(instance.SubstrateAmount.split(' ')[0]) * instance.SubstrateQuantity;
        substrateAmounts.push(totalQuatity + ' ' + instance.SubstrateAmount.split(' ')[1]);
      }
    });

    const substrateAmountSWithUom: any[] = [];
    substrateAmounts.map((amount: string) => {
      const splitAmout = amount.split(' ');
      if (splitAmout.length > 1) {
        substrateAmountSWithUom.push({
          amount: parseFloat(splitAmout[0]),
          uom: splitAmout[1]
        });
      }
    });

    this.pageVars.isReady = false;
    firstValueFrom(this.uomApiService.getUnitOfMeasuresAll())
      .then((response) => {
        const lUom: any = response.find((uom: any) => uom.name.toLowerCase() === 'l');
        let finalAmountInMl: number = 0;
        substrateAmountSWithUom.map((item) => {
          const uomObj = response.find((uom: any) => uom.name.toLowerCase() === item.uom.toLowerCase());
          if (uomObj != null && lUom != null) {
            const conversionResult = Number(lUom.conversionFactor) / Number(uomObj.conversionFactor);
            const convertedAmount = item.amount / conversionResult;
            finalAmountInMl += convertedAmount;
          }
        });
        const uomObjOa: any = response.find((uom: any) => uom.name.toLowerCase() === 'l');
        const mlUom: any = response.find((uom: any) => uom.name.toLowerCase() === 'ml');
        const conversionResultOa = Number(uomObjOa.conversionFactor) / Number(mlUom.conversionFactor);
        const converedOVerageAmount = this.amountOfOverage / conversionResultOa;
        finalAmountInMl += converedOVerageAmount;

        this.getRecipeIngredients(this.selectedOrderItemInstances[0].RecipeJson.recipeId, finalAmountInMl);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'CalculateBatchRecipeUom' });
      });
  }

  getRecipeIngredients(recipeId: string, targetValue: number) {
    const request = {
      ingredients: this.modsApiService.getRecipeIngredients(recipeId, targetValue)
    };

    this.pageVars.isReady = false;
    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.downloadRecipeIngredientsPdf(response.ingredients);
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'SubstratePrepComponent', action: 'GetRecipeIngredients' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  downloadRecipeIngredientsPdf(recipe: any): void {
    const title = recipe?.recipeName || 'Recipe';
    const fileName = this.toFileName(title);
    const footerPrivacyText = recipe.isSpecialControl ? '-- Special Control --' : '-- Confidential --';

    const docDefinition: any = {
      info: { title },
      pageMargins: [40, 60, 40, 60],
      header: () => ({
        margin: [40, 20, 40, 0],
        fontSize: 9,
        color: '#555',
        columns: [
          { text: `Recipe ID: ${recipe.recipeId}`, alignment: 'left' },
          { text: `${title}`, alignment: 'right', bold: true }
        ]
      }),
      footer: (currentPage: any, pageCount: any) => ({
        margin: [40, 0, 40, 20],
        fontSize: 9,
        color: '#555',
        columns: [
          { text: `Sterilization: ${recipe.sterilizationType}`, alignment: 'left' },
          { text: footerPrivacyText, alignment: 'center', bold: true, fontSize: 12, color: 'black' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right' }
        ]
      }),
      content: [
        { text: title, style: 'h1', margin: [0, 0, 0, 6] },
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }] },
        {
          columns: [
            {
              width: '*',
              stack: [
                this.kv('Base Volume', `${recipe.baseVolume} ${recipe.volumeUnit}`),
                this.kv('Target Volume', `${recipe.targetVolume} ${recipe.volumeUnit}`)
              ]
            },
            {
              width: '*',
              stack: [this.kv('Scaling Factor', `${recipe.scalingFactor}`), this.kv('Sterilization Type', recipe.sterilizationType)]
            }
          ],
          columnGap: 16,
          margin: [0, 10, 0, 16]
        },
        { text: 'Ingredients', style: 'h2', margin: [0, 0, 0, 8] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 100, 100, 80],
            body: [
              [
                { text: 'Chemical', style: 'th' },
                { text: 'Base Amt', style: 'th' },
                { text: 'Scaled Amt', style: 'th' },
                { text: 'Unit', style: 'th' }
              ],
              ...recipe.ingredients
                .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map((ing: any) => [ing.chemicalName || '-', this.num(ing.baseAmount), this.num(ing.scaledAmount), ing.unit || '-'])
            ]
          },
          layout: {
            fillColor: (rowIndex: number) => (rowIndex === 0 ? '#f2f2f2' : null)
          }
        },
        { text: '\nNotes', style: 'h2', margin: [0, 10, 0, 4] },
        { text: '—', style: 'notes' }
      ],
      styles: {
        h1: { fontSize: 18, bold: true },
        h2: { fontSize: 12, bold: true, color: '#333' },
        kvKey: { bold: true, color: '#555' },
        th: { bold: true },
        notes: { fontSize: 10, color: '#666' }
      },
      defaultStyle: { fontSize: 10 }
    };

    pdfMake.createPdf(docDefinition).download(`${fileName}.pdf`);
    successMsg(this.msgService, 'PDF downloaded successfully, Please check in Downloads.');
    this.onCancelBatchRecipe();
  }

  private kv(key: string, val: string) {
    return {
      margin: [0, 2, 0, 2],
      columns: [
        { width: 110, text: `${key}:`, style: 'kvKey' },
        { width: '*', text: val }
      ]
    };
  }

  private num(n: number | string | undefined) {
    const v = typeof n === 'number' ? n : Number(n);
    return isFinite(v) ? v.toString() : '-';
  }

  private toFileName(s: string) {
    return s.replace(/[^\w\d-_]+/g, '_').slice(0, 80) || 'Recipe';
  }

  getRecipesLookup() {
    this.pageVars.isReady = false;
    firstValueFrom(this.modsApiService.getRecipesLookup())
      .then((response) => {
        console.log(response);
        this.isModsRecipeEnabled = true;
        this.pageVars.isReady = true;
      })
      .catch((err) => {
        if (err.status === 403) {
          this.isRecipesLookupForbidden = true;
        }
        this.isModsRecipeEnabled = false;
        this.pageVars.isReady = true;
      });
  }
}